/**
 * BillingInvoicePrint — clean printable invoice, save-to-PDF via browser.
 *
 * Reachable by parent who owns it OR an admin of the org. Uses print
 * stylesheet (`@media print`) so any browser can save it as a PDF.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api } from "../lib/api";

function money(amount, currency = "AED") {
  return `${currency} ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function BillingInvoicePrint() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/billing/invoices/${id}/print`);
        setInv(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't load invoice.");
      }
    })();
  }, [id]);

  if (error) {
    return (
      <main className="max-w-2xl mx-auto p-8">
        <p className="font-serif italic text-[#A11D1D]">{error}</p>
      </main>
    );
  }
  if (!inv) {
    return (
      <main className="max-w-2xl mx-auto p-8">
        <p className="font-serif italic text-[#6B7280]">One quiet moment…</p>
      </main>
    );
  }

  return (
    <main
      data-testid="billing-invoice-print"
      className="min-h-screen bg-[#FAF9F1] py-10 print:bg-white"
    >
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 14mm; }
          .no-print { display: none !important; }
        }
        @media screen {
          .print-only { display: none; }
        }
      `}</style>
      <div className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none border border-[#EEF0F4] print:border-0">
        <div className="p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#1F3A2A]">Tax Invoice</div>
              <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]" data-testid="print-invoice-number">
                {inv.invoice_number}
              </h1>
              <div className="mt-1 text-xs text-[#6B7280]">
                Issued {inv.issued_at?.slice(0, 10) || "—"} · Due {inv.due_at || "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-xl text-[#1A1B2E]">Alouvya</div>
              <div className="text-xs text-[#6B7280] mt-0.5">Calm childcare operations</div>
              <button
                onClick={() => window.print()}
                data-testid="billing-print-button"
                className="no-print mt-3 inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-xs font-medium px-3 py-1.5 hover:bg-[#132C17]"
              >
                <Printer size={12} strokeWidth={1.8} /> Print / save PDF
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-8 text-sm">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Billed to</div>
              <div className="mt-1 text-[#1A1B2E] font-medium">{inv.parent_name}</div>
              {inv.parent_email && <div className="text-[#6B7280]">{inv.parent_email}</div>}
              <div className="text-[#6B7280] mt-1">For: {inv.child_name}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Period</div>
              <div className="mt-1 text-[#1A1B2E]">{inv.period_start || "—"} → {inv.period_end || "—"}</div>
              {inv.room_name && (
                <div className="text-[#6B7280] mt-1">Room: {inv.room_name}</div>
              )}
            </div>
          </div>

          <table className="mt-8 w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-[#1A1B2E]/15">
                <th className="text-left pb-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Description</th>
                <th className="text-right pb-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Qty</th>
                <th className="text-right pb-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Unit</th>
                <th className="text-right pb-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Tax</th>
                <th className="text-right pb-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Total</th>
              </tr>
            </thead>
            <tbody>
              {(inv.line_items || []).map((li) => (
                <tr key={li.id} className="border-b border-[#EEF0F4]">
                  <td className="py-3 pr-2 align-top">
                    <div className="text-[#1A1B2E]">{li.label}</div>
                    {li.description && <div className="text-xs text-[#9CA3AF]">{li.description}</div>}
                    {li.period_start && (
                      <div className="text-[10px] text-[#9CA3AF]">{li.period_start} → {li.period_end}</div>
                    )}
                  </td>
                  <td className="py-3 text-right tabular-nums">{li.quantity}</td>
                  <td className="py-3 text-right tabular-nums">{money(li.unit_amount, inv.currency)}</td>
                  <td className="py-3 text-right tabular-nums">{money(li.tax_amount, inv.currency)}</td>
                  <td className="py-3 text-right tabular-nums font-medium">{money(li.total, inv.currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="py-2 text-right text-[#6B7280]">Subtotal</td>
                <td className="py-2 text-right tabular-nums">{money(inv.subtotal, inv.currency)}</td>
              </tr>
              {inv.discount_total > 0 && (
                <tr>
                  <td colSpan={4} className="py-2 text-right text-[#6B7280]">Discounts</td>
                  <td className="py-2 text-right tabular-nums">- {money(inv.discount_total, inv.currency)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={4} className="py-2 text-right text-[#6B7280]">Tax</td>
                <td className="py-2 text-right tabular-nums">{money(inv.tax_total, inv.currency)}</td>
              </tr>
              <tr className="border-t border-[#1A1B2E]/15">
                <td colSpan={4} className="pt-3 text-right font-display text-lg text-[#1A1B2E]">Total</td>
                <td className="pt-3 text-right tabular-nums font-display text-xl text-[#1A1B2E]">
                  {money(inv.total, inv.currency)}
                </td>
              </tr>
              {inv.amount_paid > 0 && (
                <tr>
                  <td colSpan={4} className="py-2 text-right text-[#1F8A3F]">Paid</td>
                  <td className="py-2 text-right tabular-nums text-[#1F8A3F]">{money(inv.amount_paid, inv.currency)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={4} className="py-2 text-right font-display text-base text-[#1A1B2E]">Balance due</td>
                <td className="py-2 text-right tabular-nums font-display text-xl text-[#1F3A2A]">
                  {money(inv.amount_due, inv.currency)}
                </td>
              </tr>
            </tfoot>
          </table>

          {(inv.subsidy_splits || []).length > 0 && (
            <div className="mt-6 text-sm">
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6B7280]">Subsidy splits</div>
              <ul className="mt-2 space-y-1">
                {inv.subsidy_splits.map((s, i) => (
                  <li key={i} className="flex justify-between text-[#1A1B2E]">
                    <span>{s.payer_name} {s.reference ? `(ref ${s.reference})` : ""}</span>
                    <span className="tabular-nums">{money(s.amount, inv.currency)} · {s.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {inv.notes && (
            <p className="mt-6 text-xs text-[#6B7280] font-serif italic">{inv.notes}</p>
          )}
          <p className="mt-12 text-[10px] text-[#9CA3AF] tracking-[0.15em] uppercase">
            Generated by Alouvya · Calm childcare operations
          </p>
        </div>
      </div>
    </main>
  );
}
